/**
 * Camada 3 de 3 — o tema do MUI.
 *
 * Consome SEMÂNTICO. Nenhum hexadecimal nasce aqui; nenhuma primitiva é
 * lida diretamente (a exceção é `neutro`, para os papéis do MUI que pedem
 * uma escala de cinza — e mesmo assim via o token, não via literal).
 *
 * Defaults globais, todos deliberados:
 *  - botão `small` com `textTransform: 'none'` — CAIXA ALTA custa 10% de
 *    velocidade de leitura e esta ferramenta é lida o dia inteiro;
 *  - campo `small` `outlined` e tabela `small` — densidade é o requisito;
 *  - `:focus-visible` com anel de 2px da primária, e `outline: none` é
 *    PROIBIDO (o teclado é o caminho de quem processa fila);
 *  - `prefers-reduced-motion` desliga transição e animação.
 */

import { createTheme, type Theme } from '@mui/material/styles';

import './augmentacao';
import { borda, fila, sla, superficie, texto } from '../tokens/cores.semanticas';
import { neutro, erro as erroPrimitivo, pessego, sucesso as sucessoPrimitivo, teal } from '../tokens/cores.primitivas';
import { DENSIDADE_PADRAO, densidades, type ModoDeDensidade } from '../tokens/densidade';
import { espessuraDoFoco, raio, sombra } from '../tokens/forma';
import {
  celulaDeTabela,
  familiaDeIdentificador,
  familiaDeInterface,
  numerosTabulares,
  pesos,
  tamanhoBase,
} from '../tokens/tipografia';

export function criarTema(modo: ModoDeDensidade = DENSIDADE_PADRAO): Theme {
  const d = densidades[modo];

  return createTheme({
    anery: {
      densidade: modo,
      alturaDeLinha: d.alturaDeLinha,
      alturaDeCabecalho: d.alturaDeCabecalho,
      paddingDaCelula: d.paddingDaCelula,
      espacoEntreBlocos: d.espacoEntreBlocos,
      familiaDeIdentificador,
    },

    palette: {
      mode: 'light',
      primary: { main: superficie.primaria, contrastText: texto.inverso },
      secondary: { main: superficie.acento, contrastText: texto.inverso },
      error: {
        main: superficie.negativa,
        dark: erroPrimitivo.tinta,
        light: erroPrimitivo.claro,
        contrastText: texto.inverso,
      },
      success: {
        main: superficie.positiva,
        dark: sucessoPrimitivo.tinta,
        light: sucessoPrimitivo.claro,
        contrastText: texto.inverso,
      },
      warning: { main: pessego.borda, dark: pessego.texto, light: pessego.claro, contrastText: texto.inverso },
      info: { main: teal.borda, dark: teal.texto, light: teal.claro, contrastText: texto.inverso },
      text: {
        primary: texto.primario,
        secondary: texto.secundario,
        disabled: texto.desabilitado,
      },
      background: {
        default: superficie.pagina,
        paper: superficie.cartao,
      },
      divider: borda.sutil,
      sla,
      fila,
      marca: {
        teal: { fundo: superficie.teal, tinta: texto.sobreTeal, borda: borda.teal },
        pessego: { fundo: superficie.pessego, tinta: texto.sobrePessego, borda: borda.pessego },
      },
    },

    shape: { borderRadius: raio.pequeno },

    typography: {
      fontFamily: familiaDeInterface,
      fontSize: tamanhoBase,
      // Sem tamanho de display: o maior é 22px. Título grande demais rouba
      // a hierarquia da linha de tabela, que é o que a pessoa veio ler.
      h1: { fontSize: '1.375rem', lineHeight: 1.3, fontWeight: pesos.destaque },
      h2: { fontSize: '1.125rem', lineHeight: 1.35, fontWeight: pesos.forte },
      h3: { fontSize: '1rem', lineHeight: 1.4, fontWeight: pesos.forte },
      h4: { fontSize: '0.9375rem', lineHeight: 1.4, fontWeight: pesos.forte },
      h5: { fontSize: '0.875rem', lineHeight: 1.4, fontWeight: pesos.forte },
      h6: { fontSize: '0.8125rem', lineHeight: 1.4, fontWeight: pesos.forte, letterSpacing: '0.02em' },
      subtitle1: { fontSize: '0.875rem', lineHeight: 1.4, fontWeight: pesos.medio },
      subtitle2: { fontSize: '0.8125rem', lineHeight: 1.4, fontWeight: pesos.medio },
      body1: { fontSize: '0.875rem', lineHeight: 1.45 },
      body2: { fontSize: '0.8125rem', lineHeight: 1.4 },
      button: { fontSize: '0.8125rem', lineHeight: 1.4, fontWeight: pesos.forte, textTransform: 'none' },
      caption: { fontSize: '0.75rem', lineHeight: 1.35 },
      overline: {
        fontSize: '0.6875rem',
        lineHeight: 1.3,
        fontWeight: pesos.forte,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
      },
      celula: { ...celulaDeTabela, ...numerosTabulares },
      identificador: {
        fontFamily: familiaDeIdentificador,
        fontSize: '0.75rem',
        lineHeight: 1.3,
        ...numerosTabulares,
      },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: superficie.pagina,
            color: texto.primario,
          },
          // Onde houver número, tabular. Coluna de valor que não alinha
          // obriga a pessoa a ler dígito por dígito.
          'td, th, input, output, time, [data-numerico]': numerosTabulares,
          // O anel de foco é o caminho de quem processa fila no teclado.
          // Ele é declarado aqui e `outline: none` é proibido no repo —
          // há um teste que varre o código atrás disso.
          ':focus-visible': {
            outline: `${String(espessuraDoFoco)}px solid ${borda.foco}`,
            outlineOffset: '2px',
          },
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': {
              animationDuration: '0.01ms !important',
              animationIterationCount: '1 !important',
              transitionDuration: '0.01ms !important',
              scrollBehavior: 'auto !important',
            },
          },
        },
      },

      MuiButton: {
        defaultProps: { size: 'small', disableElevation: true },
        styleOverrides: {
          root: { textTransform: 'none', borderRadius: raio.pequeno },
        },
      },
      MuiIconButton: { defaultProps: { size: 'small' } },
      MuiToggleButton: {
        defaultProps: { size: 'small' },
        styleOverrides: { root: { textTransform: 'none' } },
      },
      MuiTextField: { defaultProps: { size: 'small', variant: 'outlined' } },
      MuiSelect: { defaultProps: { size: 'small' } },
      MuiFormControl: { defaultProps: { size: 'small' } },
      MuiAutocomplete: { defaultProps: { size: 'small' } },
      MuiCheckbox: { defaultProps: { size: 'small' } },
      MuiRadio: { defaultProps: { size: 'small' } },
      MuiChip: { defaultProps: { size: 'small' } },
      MuiTooltip: { defaultProps: { enterDelay: 400 } },

      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: { borderColor: borda.forte },
        },
      },

      MuiTable: { defaultProps: { size: 'small' } },
      MuiTableCell: {
        styleOverrides: {
          root: {
            ...celulaDeTabela,
            ...numerosTabulares,
            padding: d.paddingDaCelula,
            borderBottomColor: borda.sutil,
          },
          head: {
            height: d.alturaDeCabecalho,
            backgroundColor: superficie.sutil,
            color: texto.secundario,
            fontWeight: pesos.forte,
            whiteSpace: 'nowrap',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: { height: d.alturaDeLinha },
        },
      },

      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: 'none' },
          outlined: { borderColor: borda.sutil },
        },
      },
      MuiCard: {
        defaultProps: { variant: 'outlined' },
        styleOverrides: { root: { boxShadow: sombra.cartao } },
      },
      MuiDialog: {
        styleOverrides: { paper: { boxShadow: sombra.sobreposicao } },
      },
      MuiAlert: {
        defaultProps: { variant: 'outlined' },
        styleOverrides: { root: { alignItems: 'flex-start' } },
      },
      MuiLink: {
        defaultProps: { underline: 'hover' },
        styleOverrides: { root: { color: texto.link, fontWeight: pesos.medio } },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: { borderRadius: raio.pequeno, minHeight: d.alturaDeLinha },
        },
      },
      MuiSkeleton: {
        defaultProps: { animation: 'wave' },
        styleOverrides: { root: { backgroundColor: neutro.chip } },
      },
    },
  });
}

/** Tema padrão — densidade compacta, como nasce a tela de fila. */
export const temaPadrao = criarTema(DENSIDADE_PADRAO);
